import { faCheckSquare } from '@fortawesome/free-regular-svg-icons';
import {
    faCopy,
    faEdit,
    faFont,
    faList,
    faMoneyCheckAlt,
    faPlus,
    faQuoteRight,
    faTable,
    faTrash,
    faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useModalManager from '@hooks/useModalManager';
import { Button, StyledCheckbox } from '@ui';
import { FormAssistent } from 'components/Formular/FormAssistent';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmedCallback } from '~/hooks/useConfirmedCallback';
import { useApi } from '../../../../hooks/useApi';

interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
    form?: any;
    /** Owning club — handed straight to the form assistant. */
    clubSlug: string;
    selected?: boolean;
    callback?: (data?: any) => any;
    onPress?: () => void;
}
export const FormularCard = ({
    form,
    clubSlug,
    selected,
    callback,
    onPress,
    className,
    onClick,
    onKeyDown,
    ...props
}: Props) => {
    const api = useApi();
    const { addModal, removeModal } = useModalManager();
    const { t } = useTranslation('form');

    // Opens the builder for this card's form.
    const editForm = () => {
        addModal({
            title: t('builder.editTitle'),
            content: (id) => (
                <FormAssistent
                    clubSlug={clubSlug}
                    form={form}
                    cb={(data) => {
                        callback?.(data);
                        removeModal(id);
                    }}
                />
            ),
            size: 100
        });
    };

    // Duplicates the form server-side, then opens the copy in the builder.
    const duplicateForm = () => {
        void api({
            url: 'form/' + form.id + '/duplicate',
            method: 'GET',
            onSuccess: (data) => {
                callback?.(data);
                addModal({
                    title: t('builder.editTitle'),
                    content: (id) => (
                        <FormAssistent
                            clubSlug={clubSlug}
                            form={data.data}
                            cb={(data) => {
                                removeModal(id);
                                callback?.(data);
                            }}
                        />
                    ),
                    size: 100
                });
            }
        });
    };

    const deleteForm = useConfirmedCallback(
        () => {
            void api({
                url: 'form/' + form.id,
                method: 'DELETE',
                onSuccess: () => {
                    callback();
                }
            });
        },
        { destructive: true }
    );

    const renderIconPerType = useCallback((type: string) => {
        switch (type) {
            case 'iban':
                return faMoneyCheckAlt;

            case 'table':
                return faTable;

            case 'wysiwyg':
                return faFont;

            case 'checkbox':
                return faCheckSquare;

            case 'select':
                return faList;

            default:
                return faQuoteRight;
        }
    }, []);

    const handleCardClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            onClick?.(event);
            onPress?.();
        },
        [onClick, onPress]
    );

    const cardClasses = [
        'tw-flex tw-h-[200px] tw-w-full tw-card-bordered tw-transition tw-duration-150 tw-ease-out focus:tw-outline-none ',
        form ? 'tw-flex-col tw-justify-between tw-text-left' : 'tw-items-center tw-justify-center tw-text-center',
        selected ? 'tw-card-selected' : '',
        className ?? ''
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div role="button" tabIndex={0} className={cardClasses} onClick={handleCardClick} {...props}>
            {form ? (
                <>
                    <div className="tw-flex tw-items-start tw-gap-3">
                        <StyledCheckbox isSelected={selected} onChange={() => onPress?.()} />
                        <h3 className="tw-line-clamp-3 tw-flex tw-flex-[1_1_auto] tw-overflow-hidden">{form.name}</h3>
                        {Boolean(form.mandatory_user_account) ? <FontAwesomeIcon icon={faUserCheck} /> : null}
                    </div>
                    <div className="tw-flex-1 tw-overflow-hidden tw-pt-2">
                        <div className="tw-grid tw-grid-cols-[auto_1fr] tw-items-center tw-gap-x-3 tw-gap-y-2">
                            {form.fields.map((field) => (
                                <React.Fragment key={field.name}>
                                    <FontAwesomeIcon size="sm" icon={renderIconPerType(field.type)} />
                                    {field.label && <span>{field.label}</span>}{' '}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                    <div className="tw-mt-4 tw-flex tw-justify-end tw-gap-2" onClick={(event) => event.stopPropagation()}>
                        <Button type="button" size="xs" color="success" onPress={duplicateForm}>
                            <FontAwesomeIcon icon={faCopy} />
                        </Button>
                        <Button type="button" size="xs" color="warning" onPress={editForm}>
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button type="button" size="xs" color="error" onPress={deleteForm}>
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </div>
                </>
            ) : (
                <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-rounded-lg tw-text-accents-7">
                    <FontAwesomeIcon icon={faPlus} size="3x" />
                    <h3>{t('builder.createTitle')}</h3>
                </div>
            )}
        </div>
    );
};
